var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { throttle } from 'lodash';
import React, { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { View } from 'react-native';
import { recentlyUsedMobilityToolAtom } from '@/atoms/User';
import { getMobilityToolDefaultValue, } from '@/constant/review';
import { UpvoteTargetTypeDto, } from '@/generated-sources/openapi';
import { UploadProgressOverlay } from '@/components/UploadProgressOverlay';
import useAppComponents from '@/hooks/useAppComponents';
import { REGISTER_TIMEOUT_MS, REQUEST_ID_HEADER, useIdempotencyKey, } from '@/hooks/useIdempotencyKey';
import { showUploadFailedToast, useImageUploadWithProgress, } from '@/hooks/useImageUploadWithProgress';
import usePost from '@/hooks/usePost';
import { USER_TUTORIAL_PROGRESS_QUERY_KEY } from '@/hooks/useUserTutorialProgress';
import { useMe } from '@/atoms/Auth';
import { updateSearchCacheForPlaceAsync } from '@/utils/SearchPlacesUtils';
import ToastUtils from '@/utils/ToastUtils';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import PlaceInfoSection from '../sections/PlaceInfoSection';
import ToiletSection from '../sections/ToiletSection';
import UserTypeSection from '../sections/UserTypeSection';
import SectionSeparator from '../sections/SectionSeparator';
import { SafeAreaWrapper } from '@/components/SafeAreaWrapper';
export default function ToiletReviewView({ place, gotoPlaceDetail, mobilityTool, }) {
    const { api } = useAppComponents();
    const queryClient = useQueryClient();
    const { userInfo } = useMe();
    const { uploadImages, uploadProgress } = useImageUploadWithProgress();
    const getIdempotencyKey = useIdempotencyKey();
    const setRecentlyUsedMobilityTool = useSetAtom(recentlyUsedMobilityToolAtom);
    const form = useForm({
        defaultValues: {
            mobilityTool: mobilityTool !== null && mobilityTool !== void 0 ? mobilityTool : getMobilityToolDefaultValue(userInfo === null || userInfo === void 0 ? void 0 : userInfo.mobilityTools),
            floor: 1,
            toiletPhotos: [],
            comment: '',
            locationComment: '',
        },
    });
    function onValid(values) {
        return __awaiter(this, void 0, void 0, function* () {
            registerPlace(values, gotoPlaceDetail);
        });
    }
    const submitMutation = usePost(['PlaceReview', 'Toilet', 'Submit'], (_a) => __awaiter(this, [_a], void 0, function* ({ values, uploaded, requestId, }) {
        return submitRegistration({
            api,
            queryClient,
            placeId: place === null || place === void 0 ? void 0 : place.id,
            values,
            uploaded,
            requestId,
        });
    }));
    const registerPlace = useMemo(() => throttle((values, afterSuccess) => __awaiter(this, void 0, void 0, function* () {
        if (submitMutation.isPending) {
            return;
        }
        setRecentlyUsedMobilityTool({
            name: values.mobilityTool,
            timestamp: Date.now(),
        });
        // 사진 URL 은 업로드마다 달라지므로 업로드 전 입력(로컬 사진 포함)으로 키를 정한다.
        const requestId = getIdempotencyKey(values);
        let uploaded;
        try {
            uploaded = yield uploadAllPhotos({ api, values, uploadImages });
        }
        catch (e) {
            showUploadFailedToast(e);
            return;
        }
        const registered = yield submitMutation.mutateAsync({
            values,
            uploaded,
            requestId,
        });
        if (!registered) {
            return;
        }
        ToastUtils.show('화장실 리뷰를 등록했어요.');
        afterSuccess();
    }), 1000), [
        api,
        place,
        uploadImages,
        getIdempotencyKey,
        setRecentlyUsedMobilityTool,
        submitMutation,
        queryClient,
    ]);
    return (_jsx(FormProvider, Object.assign({}, form, { children: _jsxs(SafeAreaWrapper, { edges: ['bottom'], children: [_jsxs(KeyboardAwareScrollView, { stickyHeaderIndices: [0], contentContainerClassName: "grow", children: [_jsx(View, { className: "border-b border-gray-20", children: _jsx(PlaceInfoSection, { name: place === null || place === void 0 ? void 0 : place.name, address: place === null || place === void 0 ? void 0 : place.address }) }), _jsx(SectionSeparator, {}), _jsx(UserTypeSection, { nickname: userInfo === null || userInfo === void 0 ? void 0 : userInfo.nickname }), _jsx(SectionSeparator, {}), _jsx(ToiletSection, { onSave: form.handleSubmit(onValid) })] }), _jsx(UploadProgressOverlay, Object.assign({}, uploadProgress))] }) })));
}
function uploadAllPhotos(_a) {
    return __awaiter(this, arguments, void 0, function* ({ api, values, uploadImages, }) {
        const images = yield uploadImages(api, values.toiletPhotos, 'TOILET_REVIEW');
        return { images };
    });
}
function submitRegistration(_a) {
    return __awaiter(this, arguments, void 0, function* ({ api, queryClient, placeId, values, uploaded, requestId, }) {
        try {
            const isNoneOrEtc = values.toiletLocationType === 'NONE' ||
                values.toiletLocationType === 'ETC';
            yield api.registerToiletReviewPost({
                placeId,
                mobilityTool: values.mobilityTool,
                toiletLocationType: values.toiletLocationType,
                entranceDoorTypes: isNoneOrEtc ? values.doorTypes : [values.doorTypes],
                comment: values.comment,
                locationComment: values.locationComment,
                imageUrls: uploaded.images,
                floor: isNoneOrEtc ? undefined : values.floor,
            }, // FIXME: update api yaml
            { headers: { [REQUEST_ID_HEADER]: requestId }, timeout: REGISTER_TIMEOUT_MS });
            queryClient.invalidateQueries({
                queryKey: ['PlaceDetailV2', placeId, UpvoteTargetTypeDto.ToiletReview],
            });
            queryClient.invalidateQueries({
                queryKey: ['MyReviews', UpvoteTargetTypeDto.ToiletReview],
            });
            queryClient.invalidateQueries({
                queryKey: ['ReviewsUpvoted', UpvoteTargetTypeDto.ToiletReview],
            });
            queryClient.invalidateQueries({
                queryKey: ['ReviewHistory', 'Review', UpvoteTargetTypeDto.ToiletReview],
            });
            queryClient.invalidateQueries({
                queryKey: ['ReviewHistory', 'Upvote', UpvoteTargetTypeDto.ToiletReview],
            });
            // 윌리의 외출 NUX 튜토리얼: 리뷰 작성 미션 진행 상태 무효화
            queryClient.invalidateQueries({
                queryKey: USER_TUTORIAL_PROGRESS_QUERY_KEY,
            });
            updateSearchCacheForPlaceAsync(api, queryClient, placeId);
            return true;
        }
        catch (error) {
            ToastUtils.showOnApiError(error);
            return false;
        }
    });
}
